import React from 'react';
import {
    Brain,
    Cpu,
    Users,
    Handshake,
    ArrowRight,
} from 'lucide-react';

const stages = [
    {
        number: '01',
        status: 'ESTABLISHED',
        title: 'Evaluated ML foundation',
        description:
            'Our first EEG model has been developed and evaluated using research EEG data, establishing the initial cognitive inference foundation.',
        icon: Brain,
        active: false,
    },
    {
        number: '02',
        status: 'BUILDING NOW',
        title: 'Working live-EEG prototype',
        description:
            'Connect the model to real-time EEG and demonstrate the complete signal-to-inference loop outside the research dataset.',
        icon: Cpu,
        active: true,
    },
    {
        number: '03',
        status: 'NEXT',
        title: 'Human validation',
        description:
            'Test the system with new participants and validate whether the learned cognitive signals generalize beyond the research data.',
        icon: Users,
        active: false,
    },
    {
        number: '04',
        status: 'THEN',
        title: 'Device integration',
        description:
            'Work toward integrating the intelligence layer with compatible head-worn hardware and early device partners.',
        icon: Handshake,
        active: false,
    },
];

const TodayNextVision = () => {
    return (
        <section
            id="roadmap"
            className="relative overflow-hidden bg-[#080a0f] py-20 text-white sm:py-24"
        >

            {/* Background atmosphere */}
            <div className="pointer-events-none absolute inset-0">

                <div className="absolute left-1/2 top-1/3 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-[#3157ff]/[0.035] blur-[160px]" />

                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#05070b]/40" />

            </div>

            <div className="relative z-10 mx-auto max-w-[1400px] px-8">

                {/* Heading */}
                <div className="max-w-[900px]">

                    <div className="mb-6 flex items-center gap-3 text-[11px] tracking-[0.28em] text-neutral-500">
                        <span className="h-px w-8 bg-neutral-600" />
                        TODAY · NEXT · VISION
                    </div>

                    <h2 className="text-[40px] font-semibold leading-[1.03] tracking-[-0.04em] sm:text-[52px] lg:text-[60px]">
    <span className="text-neutral-500">
        The foundation is built.
    </span>
                        <br />
                        Now we prove it in the real world.
                    </h2>

                    <p className="mt-6 max-w-[720px] text-[16px] leading-[1.7] text-neutral-400 sm:text-[17px]">
                        FroCort's path is deliberate: establish the model
                        foundation, connect it to live EEG, validate it with
                        people, and then move toward device integration.
                    </p>

                </div>

                {/* Progress line */}
                <div className="mt-14 hidden lg:flex items-center">

                    {stages.map((stage, index) => (
                        <React.Fragment key={stage.number}>

                            <div
                                className={`h-1.5 w-1.5 rounded-full ${
                                    stage.active
                                        ? 'bg-[#8ea5ff]'
                                        : 'bg-neutral-700'
                                }`}
                            />

                            {index < stages.length - 1 && (
                                <div className="h-px flex-1 bg-white/10" />
                            )}

                        </React.Fragment>
                    ))}

                </div>

                {/* Stages */}
                <div className="mt-4 grid grid-cols-1 gap-px overflow-hidden rounded-[28px] border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">

                    {stages.map((stage) => {
                        const Icon = stage.icon;

                        return (
                            <article
                                key={stage.number}
                                className={`relative min-h-[300px] p-7 sm:p-8 ${
                                    stage.active
                                        ? 'bg-[#10162a]'
                                        : 'bg-[#0b0d14]'
                                }`}
                            >

                                {/* Number + status */}
                                <div className="flex items-start justify-between gap-4">

                                    <div
                                        className={`text-[10px] tracking-[0.24em] ${
                                            stage.active
                                                ? 'text-[#8ea5ff]'
                                                : 'text-neutral-600'
                                        }`}
                                    >
                                        {stage.number}
                                    </div>

                                    <div
                                        className={`rounded-full border px-2.5 py-1 text-[9px] tracking-[0.18em] ${
                                            stage.active
                                                ? 'border-[#8ea5ff]/25 bg-[#8ea5ff]/[0.06] text-[#8ea5ff]'
                                                : 'border-white/10 text-neutral-600'
                                        }`}
                                    >
                                        {stage.status}
                                    </div>

                                </div>

                                {/* Icon */}
                                <div
                                    className={`mt-12 flex h-10 w-10 items-center justify-center rounded-xl border ${
                                        stage.active
                                            ? 'border-[#8ea5ff]/20 bg-[#8ea5ff]/[0.06] text-[#8ea5ff]'
                                            : 'border-white/10 bg-white/[0.02] text-neutral-600'
                                    }`}
                                >
                                    <Icon size={18} strokeWidth={1.5} />
                                </div>

                                {/* Title */}
                                <h3 className="mt-6 text-[20px] font-medium leading-[1.25] tracking-[-0.02em]">
                                    {stage.title}
                                </h3>

                                {/* Description */}
                                <p className="mt-3 text-[13px] leading-[1.7] text-neutral-500">
                                    {stage.description}
                                </p>

                            </article>
                        );
                    })}

                </div>

                {/* Proof statement */}
                <div className="mt-12 grid gap-8 border-t border-white/10 pt-7 sm:grid-cols-[1fr_auto] sm:items-end">

                    <div>

                        <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-600">
                            The next proof point
                        </div>

                        <p className="mt-4 max-w-[850px] text-[19px] font-medium leading-[1.45] tracking-[-0.02em] text-neutral-300 sm:text-[22px]">
    <span className="text-neutral-500">
        We are not trying to jump straight to mass-market hardware.
    </span>
                            {' '}
                            We're building the evidence that makes a partner-ready product possible.
                        </p>

                    </div>

                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-neutral-600">
                        <span>Model</span>
                        <ArrowRight size={13} />
                        <span>Prototype</span>
                        <ArrowRight size={13} />
                        <span>Validation</span>
                    </div>

                </div>

                {/* Long-term opportunity */}
                <div className="mt-14 rounded-[26px] border border-white/10 bg-[#05070b] p-7 sm:p-9">

                    <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">

                        <div>

                            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-600">
                                Long-term opportunity
                            </div>

                            <h3 className="mt-4 text-[27px] font-medium leading-[1.2] tracking-[-0.025em] sm:text-[32px]">
    <span className="text-neutral-500">
        EEG is the beginning.
    </span>
                                <br />
                                Multimodal intelligence is the destination.
                            </h3>

                            <p className="mt-4 max-w-[700px] text-[14px] leading-[1.7] text-neutral-600">
                                Over time, FroCort can combine brain signals with
                                physiological and behavioral context to build a
                                richer understanding of the person.
                            </p>

                        </div>

                        {/* Signal progression */}
                        <div className="flex flex-wrap items-center gap-2 text-[11px]">

                            <span className="rounded-full border border-[#8ea5ff]/20 bg-[#8ea5ff]/[0.05] px-4 py-2 text-[#8ea5ff]">
                                EEG
                            </span>

                            <ArrowRight
                                size={14}
                                className="text-neutral-700"
                            />

                            <span className="rounded-full border border-white/10 px-4 py-2 text-neutral-500">
                                Brain + Body
                            </span>

                            <ArrowRight
                                size={14}
                                className="text-neutral-700"
                            />

                            <span className="rounded-full border border-white/10 px-4 py-2 text-neutral-500">
                                Adaptive wearables
                            </span>

                        </div>

                    </div>

                </div>

            </div>
        </section>
    );
};

export default TodayNextVision;